// ── Domain types ──────────────────────────────────────────────────────────────

export type Subject = 'Math' | 'ELA' | 'Science'
export type PassId = 'pass1' | 'pass2' | 'pass3'
export type PromptSlot = 'extract' | 'generate'
export type KuType = 'fact' | 'concept' | 'procedure' | 'process' | 'principle' | 'unclear'
export type KuStatus = 'draft' | 'inReview' | 'accepted' | 'rejected'
export type Provider = 'anthropic' | 'openai' | 'google'
export type UserRole = 'teacher' | 'researcher' | 'admin' | 'expert'
export type ConditionPhase = 'drafting' | 'running' | 'locked' | 'in_review' | 'reviewed'

export interface Standard {
  id: string
  text: string
}

export interface KnowledgeUnit {
  id: string
  run_id: string
  standard_id: string
  pass_id: PassId
  name: string
  description: string
  proposed_type: KuType | null
  type: KuType | null
  status: KuStatus
  canonical_id: string | null
  discovery_method: 'extraction' | 'traversal'
  // SME validation
  reviewer_id: string | null
  sme_valid: 'yes' | 'no' | 'unclear' | null
  sme_type_fit: 'yes' | 'no' | 'borderline' | null
  sme_corrected_type: string | null
  sme_framework_fit: 'clearly_fits' | 'forced_fit' | 'does_not_fit' | null
  sme_issue_type: 'mixed_types' | 'implicit_knowledge' | 'too_granular' | 'too_broad' | 'other' | null
  sme_notes: string | null
  // Internal
  admin_decision: 'accepted' | 'inReview' | 'rejected' | null
  admin_note: string | null
  tags: string[]
  experiment_id: string | null
  condition_id: string | null
  created_at: string
  updated_at: string
  // Joined
  votes?: Vote[]
  run?: Run
  expert_reviews?: ExpertReview[]
}

export interface Vote {
  id: string
  ku_id: string
  user_id: string
  score: number
  comment: string | null
  created_at: string
}

export interface Run {
  id: string
  user_id: string
  standard_id: string
  pass_id: PassId
  provider: Provider
  model: string
  prompt_slot: PromptSlot
  prompt_label: string
  prompt_text: string
  source_doc: string | null
  ku_count_target: number
  status: 'pending' | 'running' | 'complete' | 'error'
  error_text: string | null
  latency_ms: number | null
  input_tokens: number | null
  output_tokens: number | null
  experiment_id: string | null
  condition_id: string | null
  system_prompt_snapshot: string | null
  framework_definitions_snapshot: string | null
  created_at: string
}

export interface Prompt {
  id: string
  pass_id: PassId
  slot: PromptSlot
  label: string
  role_description: string
  text: string
  is_active: boolean
  version: number
  parent_id: string | null
  method_id: string | null
  created_by: string | null
  created_at: string
}

export interface PromptMethod {
  id: string
  name: string
  description: string | null
  is_default: boolean
  sort_order: number
  experiment_id: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface Dataset {
  id: string
  user_id: string
  name: string
  description: string | null
  created_at: string
  updated_at: string
  standard_ids?: string[]
}

// ── Experiment configuration types ────────────────────────────────────────────

export interface TaxonomyConfig {
  framework: string // "CDT", "Bloom's", "CoL", etc.
  types: string[] // ["fact","concept","procedure","process","principle","unclear"]
  typeLabels?: Record<string, string>
  typeColors?: Record<string, string>
}

export interface MaterialLevel {
  id: string // "pass1", "pass2", "pass3"
  label: string // "Requirement", "Content", "Knowledge"
  hasGenerate: boolean
  sourceDescription?: string
}

export interface SamplingStrategy {
  method: 'stratified' | 'random' | 'handpick' | 'all' | 'random_grades'
  stratifyBy?: 'grade' | 'domain'
  samplePct?: number
  sampleCount?: number
  perStratum?: number
  nGrades?: number
}

export interface EvaluationDimension {
  id: string
  label: string
  type: 'choice' | 'taxonomy_select' | 'text' | 'boolean'
  options?: string[]
  showWhen?: Record<string, string>
  required?: boolean
}

export type AnalysisMetricId =
  | 'coverage_rate'
  | 'type_distribution'
  | 'agreement_kappa'
  | 'confusion_matrix'
  | 'cross_level_consistency'
  | 'ambiguity_rate'
  | 'gap_types'
  | 'compound_units'
  | 'condition_comparison'

export type ExperimentPhase = 'design' | 'collection' | 'analysis' | 'complete'
export type ExperimentStatus = 'draft' | 'active' | 'complete' | 'archived'
// Three-layer architecture audit modes:
//   platform_validation   — reviewer rates atom quality only (Atomicity,
//                           Self-containment, Source-fidelity).
//   theory_classification — reviewer rates taxonomy fit only (for the
//                           theorist; his authority is bounded to his theory).
//   full                  — both, in the same session (legacy / research
//                           flexibility).
export type AuditMode = 'platform_validation' | 'theory_classification' | 'full'

export interface Experiment {
  id: string
  user_id: string
  name: string
  research_question: string | null
  hypothesis: string | null
  methodology_notes: string | null
  dataset_description: string | null
  conditions_description: string | null
  framework_definitions: string | null
  system_prompt: string | null
  subject: Subject
  taxonomy: TaxonomyConfig
  material_levels: MaterialLevel[]
  sampling_strategy: SamplingStrategy
  evaluation_dimensions: EvaluationDimension[]
  analysis_metrics: AnalysisMetricId[]
  phase: ExperimentPhase
  status: ExperimentStatus
  version: number
  started_at: string | null
  copy_source_experiment_id: string | null
  audit_mode: AuditMode
  created_at: string
  updated_at: string
  // Hydrated from experiment_standards junction table
  standards: string[]
  grades: string[]
}

// ── Conditions ────────────────────────────────────────────────────────────────

export interface LockedPromptSnapshot {
  source_prompt_id: string
  method_id: string | null
  label: string
  text: string
}

export type LockedPromptsByPass = Partial<Record<PassId, Partial<Record<PromptSlot, LockedPromptSnapshot>>>>

export interface Condition {
  id: string
  experiment_id: string
  name: string
  description: string | null
  sort_order: number
  phase: ConditionPhase
  locked_prompts: LockedPromptsByPass | null
  started_at: string | null
  locked_at: string | null
  created_at: string
}

export interface ExperimentReviewer {
  experiment_id: string
  reviewer_id: string
  invited_at: string
}

// ── KU cross-references (Analysis tab) ─────────────────────────────
// Manual mappings between KUs used to visualize containment and compute
// cross-level analysis metrics. Six relation types:
//
//   STRUCTURAL (directional, cross-level):
//     addresses    — A (CONT) addresses B (REQ)
//     grounds_in   — A (CONT) grounded in B (KNOW)
//
//   SEMANTIC IDENTITY (symmetric):
//     same_as      — A and B describe the same cognitive element.
//                    Classification disagreement is derived by checking if
//                    the two KUs have different proposed_type/type values.
//
//   HIERARCHY (directional):
//     subset_of    — A is more specific than B (A ⊂ B). Within-pass this
//                    reads as conceptual narrowness; cross-pass this reads
//                    as decomposition (fan-in to a single B = "split of B").
//                    The two interpretations are derived from pass_ids at
//                    analysis time; one relation type suffices.
//
//   WEAK (symmetric):
//     related_to   — some semantic overlap, not identity or subset.
export type CrossRefRelation = 'addresses' | 'grounds_in' | 'same_as' | 'subset_of' | 'related_to'

export interface CrossRefRelationMeta {
  value: CrossRefRelation
  label: string
  shortLabel: string // for buttons
  directional: boolean
  color: string
  description: string
}

export const CROSS_REF_RELATIONS: CrossRefRelationMeta[] = [
  {
    value: 'same_as',
    label: 'Same KU',
    shortLabel: 'Same',
    directional: false,
    color: '#16a34a',
    description: 'Both KUs describe the same cognitive element.',
  },
  {
    value: 'subset_of',
    label: 'Subset of',
    shortLabel: 'A ⊂ B',
    directional: true,
    color: '#0369a1',
    description: 'A is narrower/more specific than B.',
  },
  {
    value: 'addresses',
    label: 'Addresses',
    shortLabel: 'A addr. B',
    directional: true,
    color: '#0284c7',
    description: 'A (Content) addresses requirement B (Requirement).',
  },
  {
    value: 'grounds_in',
    label: 'Grounds in',
    shortLabel: 'A grnd. B',
    directional: true,
    color: '#065f46',
    description: 'A (Content) is grounded in domain knowledge B (Knowledge).',
  },
  {
    value: 'related_to',
    label: 'Related to',
    shortLabel: 'Related',
    directional: false,
    color: '#6b7280',
    description: 'Some semantic overlap, not identity or subset.',
  },
]

export interface KuCrossRef {
  id: string
  from_ku_id: string
  to_ku_id: string
  relation_type: CrossRefRelation
  created_by: string | null
  created_at: string
}

export interface ConditionPrompt {
  id: string
  condition_id: string
  pass_id: string
  slot: PromptSlot
  prompt_id: string
}

// ── Expert Review ─────────────────────────────────────────────────────────────

// Shape of the `responses` JSON blob on expert_reviews. Driven by the
// two-step review UI:
//   Step 1 (atom quality, theory-neutral, Likert 1-5 per invariant)
//   Step 2 (classification, theory-bound, expert blinded to LLM's type)
// All fields optional so partial progress is representable.
export type LikertScore = 1 | 2 | 3 | 4 | 5
export type ConfidenceLevel = 'H' | 'M' | 'L'
export type ValidAnswer = 'yes' | 'no' | 'unclear'
export type FrameworkFit = 'clearly_fits' | 'forced_fit' | 'doesnt_fit'

export interface ReviewResponses {
  // Step 1 — atom quality
  atomicity_score?: LikertScore
  self_containment_score?: LikertScore
  source_fidelity_score?: LikertScore
  atom_notes?: string
  valid?: ValidAnswer
  flag_for_debrief?: boolean
  step1_saved_at?: string

  // Step 2 — classification (expert blinded to LLM's type)
  expert_type?: string // value from experiment.taxonomy.types
  confidence?: ConfidenceLevel
  framework_fit?: FrameworkFit
  classification_notes?: string
  step2_saved_at?: string
}

export interface ExpertReview {
  id: string
  ku_id: string
  experiment_id: string
  reviewer_id: string
  responses: ReviewResponses
  locked_at: string | null
  created_at: string
  updated_at: string
}

export interface Profile {
  id: string
  name: string
  role: UserRole
  primary_grade: number | null
  subject: Subject
}

// ── Role capabilities ──────────────────────────────────────────────────────────
export interface RoleCapabilities {
  canGenerate: boolean
  canDecide: boolean
  canEditKU: boolean
  canPromptLab: boolean
  canAnalytics: boolean
  canAdmin: boolean
  canReview: boolean
}

export const ROLE_CAPS: Record<UserRole, RoleCapabilities> = {
  teacher: {
    canGenerate: false,
    canDecide: false,
    canEditKU: false,
    canPromptLab: false,
    canAnalytics: false,
    canAdmin: false,
    canReview: false,
  },
  researcher: {
    canGenerate: true,
    canDecide: true,
    canEditKU: true,
    canPromptLab: true,
    canAnalytics: true,
    canAdmin: false,
    canReview: true,
  },
  admin: {
    canGenerate: true,
    canDecide: true,
    canEditKU: true,
    canPromptLab: true,
    canAnalytics: true,
    canAdmin: true,
    canReview: true,
  },
  expert: {
    canGenerate: false,
    canDecide: false,
    canEditKU: false,
    canPromptLab: false,
    canAnalytics: false,
    canAdmin: false,
    canReview: true,
  },
}

// ── Pass metadata ──────────────────────────────────────────────────────────────
export interface PassMeta {
  id: PassId
  label: string
  title: string
  color: string
  bg: string
  icon: string
  desc: string
  hasGenerate: boolean
}

export const PASS_META: PassMeta[] = [
  {
    id: 'pass1',
    label: 'Requirement',
    title: 'What the standard requires?',
    color: '#0369a1',
    bg: '#e0f2fe',
    icon: '📋',
    desc: 'Draft KUs extracted directly from the standard text — the minimum knowledge entities required for mastery.',
    hasGenerate: false,
  },
  {
    id: 'pass2',
    label: 'Content',
    title: 'What must be learned?',
    color: '#7c3aed',
    bg: '#f5f3ff',
    icon: '📚',
    desc: 'A lesson plan is generated then draft KUs are extracted — what students need to learn, not how they will be taught.',
    hasGenerate: true,
  },
  {
    id: 'pass3',
    label: 'Knowledge',
    title: 'What exists in the domain?',
    color: '#065f46',
    bg: '#ecfdf5',
    icon: '🔬',
    desc: 'A domain explanation is generated then draft KUs are extracted — what is known about this topic at this grade level.',
    hasGenerate: true,
  },
]

// ── Provider metadata ──────────────────────────────────────────────────────────
export interface ProviderMeta {
  id: Provider
  label: string
  models: { id: string; label: string }[]
}

export const PROVIDERS: ProviderMeta[] = [
  {
    id: 'anthropic',
    label: 'Anthropic',
    models: [
      { id: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
      { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
      { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    ],
  },
  {
    id: 'openai',
    label: 'OpenAI',
    models: [
      { id: 'gpt-4o', label: 'GPT-4o' },
      { id: 'gpt-4o-mini', label: 'GPT-4o Mini' },
      { id: 'o1', label: 'o1' },
    ],
  },
  {
    id: 'google',
    label: 'Google',
    models: [
      { id: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
      { id: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
    ],
  },
]

export const KU_TYPES: { value: KuType; label: string; color: string }[] = [
  { value: 'fact', label: 'Fact', color: '#0369a1' },
  { value: 'concept', label: 'Concept', color: '#7c3aed' },
  { value: 'procedure', label: 'Procedure', color: '#065f46' },
  { value: 'process', label: 'Process', color: '#0891b2' },
  { value: 'principle', label: 'Principle', color: '#b45309' },
  { value: 'unclear', label: 'Unclear', color: '#6b7280' },
]
